import { Button, Group, Paper, ScrollArea, Table, Text } from '@mantine/core'
import { IconDownload } from '@tabler/icons-react'
import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Link } from 'react-router-dom'
import { SafeHtml } from '@/components/ui/SafeHtml'
import type { ChatEntry } from '../useAssistant'
import { buildAssistantTableCsv, buildAssistantTableCsvFilename } from './assistant-table-csv'

type AssistantAnswer = NonNullable<Extract<ChatEntry, { kind: 'turn' }>['assistant']>
type TableSpec = NonNullable<NonNullable<AssistantAnswer['artifacts']>['tables']>[number]

type AssistantTableProps = {
  table: TableSpec
}

// Shared styling for every link rendered inside an assistant table cell —
// internal (patient, fax-detail) and external (guideline citations) alike stay
// visually consistent (blue + underlined).
const assistantLinkClass = 'font-medium text-[#2563eb] underline underline-offset-2'

const formatCellValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '-'
  return String(value)
}

// Treat any href that points at an in-app route (starts with '/') as an
// internal link — rendered via react-router's <Link> with the shared styling.
// External URLs fall through to a plain anchor that opens in a new tab.
const isInternalHref = (href: string | undefined): href is string =>
  typeof href === 'string' && href.startsWith('/')

// A cell that IS a markdown link — the shape the server-built link cells
// (Sent, Patient) use. Labels may carry backslash-escaped brackets
// (`\[`/`\]` — LOINC report names like "Creatinine [Mass/volume]…" are
// escaped server-side by markdownLink); an UNescaped bracket still rejects
// the cell, matching how ReactMarkdown would fail to parse it.
const MARKDOWN_LINK_PATTERN = /^\[(?:\\.|[^[\]\\])*\]\([^)]*\)$/

// A well-formed markdown link ANYWHERE in the cell — the shape model-authored
// prose cells use (e.g. a risks & gaps WHY IT MATTERS cell citing a guideline
// inline). Same label/href grammar as the whole-cell pattern; free text with a
// lone bracketed phrase (no adjacent `(url)`) still renders verbatim.
const CONTAINS_MARKDOWN_LINK_PATTERN = /\[(?:\\.|[^[\]\\])*\]\([^)]*\)/

// A cell that looks like imported HTML (some clinical fields — e.g. a visit's Reason — carry
// EHR/referral markup verbatim). Requires a real tag (a letter after `<`), so free text like
// "value < 5" or "a<b" stays plain.
const HTML_PATTERN = /<([a-z][a-z0-9]*)\b[^>]*>/i

const MULTILINE_PREVIEW_LINES = 10
const MAX_PREVIEW_CHARS = 200

// Shared anchor rendering for every markdown-rendered cell.
const markdownLinkComponents = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) =>
    isInternalHref(href) ? (
      <Link to={href} className={assistantLinkClass}>
        {children}
      </Link>
    ) : (
      <a href={href} target="_blank" rel="noopener noreferrer" className={assistantLinkClass}>
        {children}
      </a>
    ),
}

// Cell rendering heuristics:
// - the whole cell is one markdown link → render via ReactMarkdown (styled
//   internal <Link> / external <a>);
// - prose CONTAINING a markdown link → render via ReactMarkdown too (clamped on
//   the RENDERED text — a long href must not eat the preview budget);
// - imported EHR/referral markup → render sanitized via SafeHtml;
// - short single-line free text → render verbatim (notes are data, not markup);
// - anything longer than 10 lines or 200 characters → preserve line breaks and
//   clamp to the preview behind a Show more / Show less toggle.
const AssistantTableCell = ({ value }: { value: string | number | null | undefined }) => {
  const formatted = formatCellValue(value)
  if (formatted === '-') return formatted

  if (MARKDOWN_LINK_PATTERN.test(formatted)) {
    return (
      <ReactMarkdown
        components={{ ...markdownLinkComponents, p: ({ children }) => <>{children}</> }}
      >
        {formatted}
      </ReactMarkdown>
    )
  }

  if (CONTAINS_MARKDOWN_LINK_PATTERN.test(formatted)) {
    return <MarkdownProseCell text={formatted} />
  }

  // Imported HTML renders sanitized (DOMPurify allowlist) instead of showing raw tags — the
  // treatment the old Details tab gave the visit Reason column. Checked AFTER the markdown
  // branches so model-authored prose that happens to contain an angle-bracket token (e.g.
  // "<sys>") still renders as prose rather than being run through the sanitizer.
  if (HTML_PATTERN.test(formatted)) {
    return <SafeHtml html={formatted} fallback="-" />
  }

  if (!formatted.includes('\n') && formatted.length <= MAX_PREVIEW_CHARS) return formatted

  return <ClampedCell text={formatted} />
}

// The VISIBLE portion of a markdown cell for clamp decisions: hrefs don't render,
// so drop them before measuring (a guideline URL is often longer than the sentence
// citing it).
const markdownVisibleText = (text: string): string => text.replace(/\]\([^)]*\)/g, ']')

// ~10 wrapped lines at the cell's 320px width; the clamp itself is CSS
// (line-clamp), so the threshold only decides whether the toggle is offered.
const MARKDOWN_CLAMP_CHARS = 400

// A prose cell with embedded markdown link(s): rendered markdown (links, bold,
// lists) with the same Show more / Show less affordance as ClampedCell — but
// clamped via CSS on the rendered output rather than by slicing the raw text,
// which would cut a link's syntax in half.
const MarkdownProseCell = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false)
  const visible = markdownVisibleText(text)
  const clampable =
    visible.split('\n').length > MULTILINE_PREVIEW_LINES || visible.length > MARKDOWN_CLAMP_CHARS

  return (
    <div>
      <div className={clampable && !expanded ? 'line-clamp-[10]' : undefined}>
        <ReactMarkdown
          components={{
            ...markdownLinkComponents,
            p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
            ul: ({ children }) => <ul className="mb-1 list-disc pl-4 last:mb-0">{children}</ul>,
            ol: ({ children }) => <ol className="mb-1 list-decimal pl-4 last:mb-0">{children}</ol>,
          }}
        >
          {text}
        </ReactMarkdown>
      </div>
      {clampable && (
        <button
          type="button"
          className={`${assistantLinkClass} cursor-pointer`}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? 'show less' : 'show more'}
        </button>
      )}
    </div>
  )
}

// Preview = the first 10 lines, further cut to 200 characters — whichever
// clamps first.
const clampPreview = (text: string): string => {
  const lineClamped = text.split('\n').slice(0, MULTILINE_PREVIEW_LINES).join('\n')
  return lineClamped.length > MAX_PREVIEW_CHARS
    ? lineClamped.slice(0, MAX_PREVIEW_CHARS)
    : lineClamped
}

const ClampedCell = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false)
  const preview = clampPreview(text)
  const clampable = preview !== text
  const shown = clampable && !expanded ? `${preview}…` : text
  const toggle = () => setExpanded((current) => !current)

  return (
    <div
      className={clampable ? 'cursor-pointer whitespace-pre-wrap' : 'whitespace-pre-wrap'}
      onClick={
        clampable
          ? () => {
              // Don't collapse/expand under a text-selection click — let the
              // user copy from the cell without the content jumping.
              if (!window.getSelection()?.toString()) toggle()
            }
          : undefined
      }
    >
      {shown}
      {clampable && (
        <>
          {' '}
          <button
            type="button"
            className={`${assistantLinkClass} cursor-pointer`}
            onClick={(event) => {
              event.stopPropagation()
              toggle()
            }}
          >
            {expanded ? 'show less' : 'show more'}
          </button>
        </>
      )}
    </div>
  )
}

const downloadCsv = (table: TableSpec): void => {
  const blob = new Blob([buildAssistantTableCsv(table)], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = buildAssistantTableCsvFilename(table.title)
  link.click()
  URL.revokeObjectURL(url)
}

export const AssistantTable = ({ table }: AssistantTableProps) => {
  if (table.columns.length === 0) return null

  return (
    <Paper withBorder radius="md" className="overflow-hidden">
      {(table.title || table.rows.length > 0) && (
        <div className="border-b px-3.5 py-2.5">
          <Group justify="space-between" gap="sm" wrap="nowrap">
            {table.title ? (
              <Text size="sm" fw={700}>
                {table.title}
                {table.subTitle && (
                  <span className="ml-2 text-xs font-normal text-slate-500">{table.subTitle}</span>
                )}
              </Text>
            ) : (
              <span />
            )}
            {table.rows.length > 0 && (
              <Button
                size="compact-xs"
                variant="light"
                leftSection={<IconDownload size={14} />}
                onClick={() => downloadCsv(table)}
              >
                CSV
              </Button>
            )}
          </Group>
        </div>
      )}
      <ScrollArea type="auto">
        <Table highlightOnHover horizontalSpacing="sm" verticalSpacing="xs" className="min-w-max">
          <Table.Thead>
            <Table.Tr>
              {table.columns.map((column, columnIndex) => (
                <Table.Th
                  key={`${column}-${columnIndex}`}
                  className="text-xs tracking-wide whitespace-nowrap uppercase"
                >
                  {column}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {table.rows.length > 0 ? (
              table.rows.map((row, rowIndex) => (
                <Table.Tr key={rowIndex}>
                  {table.columns.map((column, columnIndex) => (
                    <Table.Td
                      key={`${column}-${columnIndex}`}
                      className="max-w-[320px] text-xs whitespace-normal"
                    >
                      <AssistantTableCell value={row[columnIndex]} />
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={table.columns.length} className="py-4 text-center text-xs">
                  No rows to display.
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Paper>
  )
}
